import { AxiosPromise } from 'axios';
import { http } from 'services';
import type {
  AudioStart,
  CreateBlockInput,
  ExamConfig,
  ExamReadiness,
  ExamSnapshot,
  IeltsBandSuggestion,
  IeltsCriterionScore,
  SectionBlockDetail,
  SectionBlockSummary,
  SectionKind,
  SectionSlotRule,
  UpdateBlockInput,
  UpdateExamSectionInput,
} from './types';

// ─── Candidate runtime ───────────────────────────────────────────────────────

export const ExamState = (attemptId: string): AxiosPromise<ExamSnapshot> =>
  http.request.get(`/api/attempts/${attemptId}/exam-state`);

export const AdvanceSection = (attemptId: string, sectionId: string): AxiosPromise<ExamSnapshot> =>
  http.request.post(`/api/attempts/${attemptId}/sections/${sectionId}/advance`);

export const ResumeBreak = (attemptId: string): AxiosPromise<ExamSnapshot> =>
  http.request.post(`/api/attempts/${attemptId}/break/resume`);

export const StartAudio = (attemptId: string, sectionId: string, track: number): AxiosPromise<AudioStart> =>
  http.request.post(`/api/attempts/${attemptId}/sections/${sectionId}/audio/${track}/start`);

/** How far a single-play Listening track has been heard; a reload resumes there. */
export const AudioProgress = (attemptId: string, sectionId: string, track: number, positionSec: number): AxiosPromise<{ heardSec: number }> =>
  http.request.post(`/api/attempts/${attemptId}/sections/${sectionId}/audio/${track}/progress`, { positionSec });

// ─── Author: exam settings ───────────────────────────────────────────────────

export const UpdateExamSettings = (
  testId: string,
  data: Partial<Pick<ExamConfig, 'ielts' | 'versions'>> & { sat?: Partial<NonNullable<ExamConfig['sat']>> },
): AxiosPromise<{ examConfig: ExamConfig }> => http.request.patch(`/api/tests/${testId}/exam`, data);

export const UpdateExamSection = (
  testId: string,
  sectionId: string,
  data: UpdateExamSectionInput,
): AxiosPromise<unknown> => http.request.patch(`/api/tests/${testId}/exam/sections/${sectionId}`, data);

export const Readiness = (testId: string): AxiosPromise<ExamReadiness> =>
  http.request.get(`/api/tests/${testId}/exam/readiness`);

/** Uploads one recorded Speaking answer; each prompt is recorded once. */
export const UploadSpeaking = (
  attemptId: string,
  sectionId: string,
  questionId: string,
  promptKey: string,
  audio: Blob,
  durationSec: number,
): AxiosPromise<{ promptKey: string; durationSec: number | null; /** The live examiner can follow this answer up: ask for it. */ followUp?: boolean }> => {
  const form = new FormData();
  const ext = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm';
  form.append('file', audio, `answer-${promptKey}.${ext}`);
  form.append('durationSec', String(Math.round(durationSec * 10) / 10));
  return http.request.post(`/api/attempts/${attemptId}/sections/${sectionId}/speaking/${questionId}/${promptKey}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/** A short-lived URL of the examiner asking one Speaking prompt; null means use the browser's voice. */
export const ExaminerAudio = (
  attemptId: string,
  sectionId: string,
  questionId: string,
  promptKey: string,
): AxiosPromise<{ url: string | null }> =>
  http.request.get(`/api/attempts/${attemptId}/sections/${sectionId}/speaking/${questionId}/${promptKey}/examiner`);

/** What the live examiner asks next: `key` null means nothing, and the scripted test carries on. */
export interface SpeakingFollowUpResult {
  key: string | null;
  text?: string;
  /** The examiner's recorded voice; null: read `text` with the browser's voice. */
  url?: string | null;
}

/** Asks the live examiner for a follow-up to a prompt the candidate has just answered. */
export const SpeakingFollowUp = (
  attemptId: string,
  sectionId: string,
  questionId: string,
  promptKey: string,
  timeoutMs: number,
): AxiosPromise<SpeakingFollowUpResult> =>
  http.request.post(`/api/attempts/${attemptId}/sections/${sectionId}/speaking/${questionId}/${promptKey}/follow-up`, undefined, {
    timeout: timeoutMs,
  });

export const AddSpeakingSection = (testId: string): AxiosPromise<{ sectionId: string }> =>
  http.request.post(`/api/tests/${testId}/exam/sections/speaking`);

export const RemoveSpeakingSection = (testId: string, sectionId: string): AxiosPromise<{ removed: boolean }> =>
  http.request.delete(`/api/tests/${testId}/exam/sections/${sectionId}/speaking`);

export const UploadAudio = (
  file: File,
  onProgress?: (percent: number) => void,
): AxiosPromise<{ success: boolean; data: { key: string; url: string; contentType: string; size: number } }> => {
  const form = new FormData();
  form.append('file', file);
  return http.request.post('/api/uploads/audio', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
};

export const SignAudio = (key: string): AxiosPromise<{ success: boolean; data: { url: string } }> =>
  http.request.post('/api/uploads/audio/sign', { key });

// ─── Section library ─────────────────────────────────────────────────────────

export const LibraryList = (params?: { kind?: SectionKind; status?: string; q?: string }): AxiosPromise<{ data: SectionBlockSummary[] }> =>
  http.request.get('/api/section-library', { params });

export const LibraryGet = (id: string): AxiosPromise<SectionBlockDetail> =>
  http.request.get(`/api/section-library/${id}`);

export const LibraryCreate = (data: CreateBlockInput): AxiosPromise<SectionBlockDetail> =>
  http.request.post('/api/section-library', data);

export const LibraryUpdate = (id: string, data: UpdateBlockInput): AxiosPromise<SectionBlockDetail> =>
  http.request.patch(`/api/section-library/${id}`, data);

export const LibraryAddQuestion = (id: string, contentId: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.post(`/api/section-library/${id}/parts/${contentId}/questions`, data);

export const LibraryUpdateQuestion = (id: string, questionId: string, data: Record<string, unknown>): AxiosPromise<unknown> =>
  http.request.patch(`/api/section-library/${id}/questions/${questionId}`, data);

export const LibraryDeleteQuestion = (id: string, questionId: string): AxiosPromise<unknown> =>
  http.request.delete(`/api/section-library/${id}/questions/${questionId}`);

export const LibraryReady = (id: string): AxiosPromise<SectionBlockDetail> =>
  http.request.post(`/api/section-library/${id}/ready`);

export const LibraryNewVersion = (id: string): AxiosPromise<SectionBlockDetail> =>
  http.request.post(`/api/section-library/${id}/new-version`);

export const LibraryArchive = (id: string): AxiosPromise<SectionBlockDetail> =>
  http.request.post(`/api/section-library/${id}/archive`);

export const LibraryRestore = (id: string): AxiosPromise<SectionBlockDetail> =>
  http.request.post(`/api/section-library/${id}/restore`);

export const LibraryDelete = (id: string): AxiosPromise<{ deleted: boolean }> =>
  http.request.delete(`/api/section-library/${id}`);

export const LibrarySaveFromTest =(testId: string, sectionId: string): AxiosPromise<SectionBlockDetail> =>
  http.request.post('/api/section-library/from-test', { testId, sectionId });

export const LibraryMatchCount = (kind: SectionKind, rule?: Omit<SectionSlotRule, 'slot'>): AxiosPromise<{ count: number }> =>
  http.request.post('/api/section-library/match-count', { kind, rule });

// ─── Grading (IELTS Writing) ─────────────────────────────────────────────────

export const GradeIeltsBands = (data: { answerId: string; criteria: IeltsCriterionScore[]; feedback?: string }): AxiosPromise<unknown> =>
  http.request.post('/api/grading/ielts-bands', data);

export const SuggestIeltsBands = (attemptId: string, questionId: string, language?: string): AxiosPromise<IeltsBandSuggestion> =>
  http.request.post(`/api/grading/${attemptId}/ai-bands/${questionId}`, { language });
